import { useRef, useState } from 'react';

import { User } from 'firebase/auth';
import { ArrowRight, KeyRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { SuccessPage } from './SuccessPage';
import { useAuth } from '../../auth/context/AuthContext';
import { AccountChoice } from '../components/AccountChoice';
import { AccountDetails } from '../components/AccountDetails';
import { RedemptionLayout } from '../components/RedemptionLayout';
import { useRedeemToken } from '../hooks/useRedeemToken';

export const RedeemPage = () => {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  if (!token) {
    return (
      <RedemptionLayout>
        <div className="redemption-result">
          <h1>{t('redemption_page.missing_token_title')}</h1>
          <p className="redemption-body">{t('redemption_page.missing_token')}</p>
          <a className="redemption-text-link" href="mailto:support@remente.com">
            {t('error_page.contact_support')}
          </a>
        </div>
      </RedemptionLayout>
    );
  }

  if (loading) {
    return (
      <RedemptionLayout step={0} showCodeNote>
        <p className="redemption-note" role="status">
          {t('auth_page.loading')}
        </p>
      </RedemptionLayout>
    );
  }

  if (!user) {
    return <AccountChoice token={token} />;
  }

  // A changed account or token must never inherit another redemption's result.
  return <AccountRedemption key={JSON.stringify([user.uid, token])} user={user} token={token} />;
};

const AccountRedemption = ({ user, token }: { user: User; token: string }) => {
  const { logout } = useAuth();
  const { t } = useTranslation();
  const redemption = useRedeemToken();
  const submitting = useRef(false);
  const [switchingAccount, setSwitchingAccount] = useState(false);
  const [switchError, setSwitchError] = useState(false);
  const busy = redemption.isPending || switchingAccount;

  const redeem = async () => {
    if (submitting.current || switchingAccount) return;
    submitting.current = true;
    try {
      await redemption.mutateAsync({ token, userId: user.uid });
    } catch {
      // The mutation retains the typed error for the retry/support UI below.
    } finally {
      submitting.current = false;
    }
  };

  const changeAccount = async () => {
    if (submitting.current || switchingAccount) return;
    setSwitchingAccount(true);
    setSwitchError(false);
    try {
      await logout();
    } catch {
      setSwitchError(true);
      setSwitchingAccount(false);
    }
  };

  if (redemption.isSuccess) {
    return <SuccessPage user={user} status={redemption.data.status} />;
  }

  const error = redemption.error?.code;
  const errorMessage =
    error === 'invalid'
      ? t('error_page.invalid_token')
      : error === 'authentication'
        ? t('error_page.authentication')
        : t('error_page.network_error');
  const canRedeem = !error || error === 'network';

  return (
    <RedemptionLayout step={1} showCodeNote>
      <div className="redemption-result">
        <div className="redemption-status-icon">
          <KeyRound size={27} aria-hidden="true" />
        </div>
        <h1>
          {error === 'invalid' ? t('error_page.invalid_token_title') : t('redemption_page.title')}
        </h1>
        <p className="redemption-body">{t('redemption_page.confirm_account')}</p>
        <AccountDetails user={user} />

        {redemption.isError && (
          <p role="alert" className="redemption-alert">
            {errorMessage}
          </p>
        )}
        {switchError && (
          <p role="alert" className="redemption-alert">
            {t('redemption_page.switch_error')}
          </p>
        )}
        {redemption.isPending && (
          <p role="status" className="redemption-note">
            {t('toast.redeeming')}
          </p>
        )}

        <div className="redemption-actions">
          {canRedeem && (
            <button
              className="redemption-primary"
              onClick={() => void redeem()}
              disabled={busy}
              aria-busy={redemption.isPending}
            >
              {error ? t('error_page.try_again') : t('redemption_page.redeem')}
              <ArrowRight size={18} aria-hidden="true" />
            </button>
          )}
          <button
            className="redemption-text-link"
            onClick={() => void changeAccount()}
            disabled={busy}
          >
            {t('redemption_page.use_another_account')}
          </button>
          {redemption.isError && (
            <a className="redemption-text-link" href="mailto:support@remente.com">
              {t('error_page.contact_support')}
            </a>
          )}
        </div>
      </div>
    </RedemptionLayout>
  );
};
